import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import "./Contact.css";
import Container from "../../components/Container";
import Sidebar from "../../components/Sidebar";

const TURNSTILE_SITE_KEY = "0x4AAAAAAEXAXf_ak_6UVl-a";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  department: "Appointments",
  message: "",
};

function validateContactForm(formData, captchaToken) {
  const errors = {};
  const name = formData.name.trim();
  const email = formData.email.trim();
  const phone = formData.phone.trim();
  const message = formData.message.trim();

  if (!name) {
    errors.name = "Please enter your name.";
  } else if (name.length < 2) {
    errors.name = "Name must be at least 2 characters.";
  }

  if (!email) {
    errors.email = "Please enter your email.";
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (!phone) {
    errors.phone = "Please enter your phone number.";
  } else if (phone.replace(/\D/g, "").length < 10) {
    errors.phone = "Please enter a 10-digit phone number.";
  }

  if (!formData.department) {
    errors.department = "Please select a department.";
  }

  if (!message) {
    errors.message = "Please enter a message.";
  } else if (message.length < 10) {
    errors.message = "Message must be at least 10 characters.";
  }

  if (!captchaToken) {
    errors.captcha = "Please complete the verification check.";
  }

  return errors;
}

const TurnstileWidget = forwardRef(function TurnstileWidget({ onToken }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const reset = () => {
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
    onTokenRef.current("");
  };

  useImperativeHandle(ref, () => ({ reset }));

  useEffect(() => {
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }
      if (widgetIdRef.current != null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        action: "contact",
        callback: (token) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(""),
        "error-callback": () => onTokenRef.current(""),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const scriptId = "cf-turnstile-script";
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  return <div ref={containerRef} className="turnstileWidget" />;
});

function Field({ error, children }) {
  return (
    <div className={`field${error ? " hasError" : ""}`}>
      {children}
      {error ? <p className="fieldError">{error}</p> : null}
    </div>
  );
}

function Contact() {
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [messageStatus, setMessageStatus] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (messageStatus.msg) {
      setMessageStatus({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = validateContactForm(formData, captchaToken);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setMessageStatus({ success: false, msg: "Please fix the highlighted fields." });
      return;
    }

    setSubmitting(true);
    setMessageStatus({});

    try {
      const response = await fetch("/handleForm.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          ...formData,
          "cf-turnstile-response": captchaToken,
        }).toString(),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = { message: "Failed to send the message. Please try again!" };
      }

      if (!response.ok || data.ok === false) {
        if (data.errors) {
          setErrors(data.errors);
        }
        setMessageStatus({
          success: false,
          msg: data.message || "Failed to send the message. Please try again!",
        });
        turnstileRef.current?.reset();
        return;
      }

      setFormData(EMPTY_FORM);
      setErrors({});
      setMessageStatus({ success: true, msg: "Message successfully sent!" });
      turnstileRef.current?.reset();
    } catch (error) {
      console.error("Error:", error);
      setMessageStatus({ success: false, msg: "Failed to send the message. Please try again!" });
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container backgroundColor="#ffffff">
      <div className="contact">
        <div className="contactForm">
          <h1>Contact Us</h1>
          <form onSubmit={handleSubmit} noValidate>
            <Field error={errors.name}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                aria-label="Your Name"
                aria-invalid={Boolean(errors.name)}
                autoComplete="name"
              />
            </Field>
            <Field error={errors.email}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                aria-label="Your Email"
                aria-invalid={Boolean(errors.email)}
                autoComplete="email"
              />
            </Field>
            <Field error={errors.phone}>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your Phone"
                aria-label="Your Phone"
                aria-invalid={Boolean(errors.phone)}
                autoComplete="tel"
              />
            </Field>
            <p><strong>Please Select One:</strong></p>
            <Field error={errors.department}>
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                aria-label="Department"
                aria-invalid={Boolean(errors.department)}
              >
                <option value="Appointments">Appointments</option>
                <option value="Medical Records">Medical Records</option>
                <option value="Billing">Billing</option>
                <option value="Medications">Medications</option>
                <option value="Residential Treatment">Residential Treatment</option>
                <option value="Other">Other</option>
              </select>
            </Field>
            <Field error={errors.message}>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                aria-label="Your Message"
                aria-invalid={Boolean(errors.message)}
              />
            </Field>
            <Field error={errors.captcha}>
              <TurnstileWidget
                ref={turnstileRef}
                onToken={(token) => {
                  setCaptchaToken(token);
                  if (token) {
                    setErrors((prev) => {
                      if (!prev.captcha) {
                        return prev;
                      }
                      const next = { ...prev };
                      delete next.captcha;
                      return next;
                    });
                  }
                }}
              />
            </Field>
            <button type="submit" disabled={submitting}>
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
          <div className={`messageStatus ${messageStatus.success ? "success" : "error"}`}>
            {messageStatus.msg}
          </div>
        </div>
        <Sidebar />
      </div>
    </Container>
  );
}

export default Contact;
